import React, { useContext, useEffect, useState } from "react";
import LandLordContext from "../../context/landlord/LandLordContext";
import useTranslator from "../../hooks/useTranslator";
import moment from "moment";
import Loader from "../Loader";
// LoadingSpinner removed — using main Loader instead
import { formatPropertyAddress, getUserDisplayName } from "../../helper/common";
import { transformAwardSnapshot } from "../../Utils/transformAwardSnapshot";
import axiosInstance from "../../services/authWrapper";
import { Badge, Button, Icon, Tabs, TextField } from "../design";
import "./PropertyHistoryTimeline.css";

const PropertyHistoryTimeline = ({
  property,
  user,
  currentLang,
  onBack,
  onViewSnapshot,
}) => {
  const { _t } = useTranslator();
  const { getPropertyHistory } = useContext(LandLordContext);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalRecords: 0,
    totalPages: 1,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [snapshotLoadingId, setSnapshotLoadingId] = useState(null);

  const workspaceId =
    user?.current_workspace?._id || user?.current_workspace?.id;

  const fetchHistory = async (activeFilter, pageNum = 1, append = false) => {
    if (!property?._id || !workspaceId) return;

    if (pageNum === 1 && !append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const res = await getPropertyHistory(property._id, {
        workspace_id: workspaceId,
        filter: activeFilter,
        page: pageNum,
        limit: 20,
      });

      if (res && res.status === "success") {
        const newEvents = res.data || [];
        setEvents((prev) => (append ? [...prev, ...newEvents] : newEvents));
        setPagination(
          res.pagination || {
            page: pageNum,
            limit: 20,
            totalRecords: newEvents.length,
            totalPages: 1,
          }
        );
      }
    } catch (err) {
      console.error("Error fetching property history:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchHistory(filter, 1, false);
  }, [property?._id, workspaceId, filter]);

  const handleFilterChange = (newFilter) => {
    if (newFilter === filter) return;
    setFilter(newFilter);
  };

  const handleLoadMore = () => {
    if (pagination.page < pagination.totalPages && !loadingMore) {
      fetchHistory(filter, pagination.page + 1, true);
    }
  };

  const getActorName = (actor) => {
    if (!actor) return "";
    if (actor.fname_companyName || actor.lname) {
      return [actor.fname_companyName, actor.lname].filter(Boolean).join(" ");
    }
    return getUserDisplayName(actor, "");
  };

  const getEventTitle = (event) => {
    const type = event?.event_type;
    if (type === "tenant_linked") {
      return _t("tenantLinked", "Tenant linked");
    }
    if (type === "tenant_unlinked") {
      return _t("tenantUnlinked", "Tenant unlinked");
    }
    if (type === "property_awarded") {
      return _t("propertyAwarded", "Property awarded");
    }
    if (type === "property_unawarded") {
      return _t("propertyUnawarded", "Property unawarded");
    }
    if (type === "insurance_purchased") {
      return _t("insurancePurchased", "Insurance purchased");
    }
    if (type === "skor_protect_active") {
      return _t("skorProtectActive", "Skor Protect active");
    }
    if (type === "application_linked") {
      return _t("applicationLinked", "Application linked");
    }
    if (type === "application_unlinked") {
      return _t("applicationUnlinked", "Application unlinked");
    }
    if (type === "shared_to") {
      return _t("sharedTo", "Shared to");
    }
    if (type === "origin") {
      return _t("origin", "Origin");
    }
    if (
      type === "copied_from" ||
      type?.includes("imported") ||
      type?.includes("inherited") ||
      event?.inherited
    ) {
      const wsName =
        event?.inherited_from_workspace?.name ||
        event?.actor_workspace?.name ||
        "";
      return wsName
        ? `${_t("importedFrom", "Imported from")} ${wsName}`
        : _t("importedFromWorkspace", "Imported from Workspace");
    }
    if (event?.title) return event.title;
    return type
      ? type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : _t("activity", "Activity");
  };

  const getEventDetails = (event) => {
    if (event?.details) return event.details;
    if (event?.meta?.details) return event.meta.details;

    const type = event?.event_type;
    if (
      type === "copied_from" ||
      type?.includes("imported") ||
      type?.includes("inherited") ||
      event?.inherited
    ) {
      return _t("tenantsAndHistoryImported", "Tenants and history imported");
    }

    if (
      Array.isArray(event?.meta?.tenant_names) &&
      event.meta.tenant_names.length > 0
    ) {
      return event.meta.tenant_names.join(", ");
    }
    if (
      event?.meta?.tenant_names &&
      typeof event.meta.tenant_names === "string"
    ) {
      return event.meta.tenant_names;
    }

    if (Array.isArray(event?.tenant_names) && event.tenant_names.length > 0) {
      return event.tenant_names.join(", ");
    }

    if (event?.tenant_names && typeof event.tenant_names === "string") {
      return event.tenant_names;
    }

    if (Array.isArray(event?.tenants) && event.tenants.length > 0) {
      return event.tenants
        .map((t) =>
          typeof t === "object"
            ? [t?.fname, t?.lname].filter(Boolean).join(" ") ||
              t?.forensic_name ||
              t?.email
            : t
        )
        .filter(Boolean)
        .join(", ");
    }

    if (event?.tenant_id) {
      const t = event.tenant_id;
      if (typeof t === "object") {
        return (
          [t?.fname, t?.lname].filter(Boolean).join(" ") ||
          t?.forensic_name ||
          t?.email ||
          ""
        );
      }
      return String(t);
    }

    return "";
  };

  const handleSnapshotClick = async (event) => {
    if (event?.event_type === "property_awarded") {
      setSnapshotLoadingId(event._id);
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/users/history-award-snapshot/${event._id}`
        );
        const snapshotData = res.data?.data || res.data;
        const profiles = transformAwardSnapshot(snapshotData);
        if (onViewSnapshot) onViewSnapshot(profiles, event._id);
      } catch (err) {
        console.error("Failed to load snapshot:", err);
      } finally {
        setSnapshotLoadingId(null);
      }
    } else if (onViewSnapshot) {
      onViewSnapshot(event);
    }
  };

  const renderNodeIcon = (event) => {
    const type = event?.event_type;

    if (type === "tenant_linked" || type === "application_linked") {
      return (
        <div className="history-node-icon icon-green">
          <Icon name="plus" size={14} />
        </div>
      );
    }

    if (
      type === "property_awarded" ||
      type === "insurance_purchased" ||
      type === "skor_protect_active"
    ) {
      return (
        <div className="history-node-icon icon-green">
          <Icon name="check" size={14} />
        </div>
      );
    }

    if (type === "shared_to") {
      return (
        <div className="history-node-icon icon-outline">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 17 17 7" />
            <path d="M8 7h9v9" />
          </svg>
        </div>
      );
    }

    if (
      type === "copied_from" ||
      type?.includes("imported") ||
      type?.includes("inherited") ||
      event?.inherited
    ) {
      return (
        <div className="history-node-icon icon-outline">
          <Icon name="edit" size={14} />
        </div>
      );
    }

    if (
      type === "tenant_unlinked" ||
      type === "property_unawarded" ||
      type === "application_unlinked"
    ) {
      return (
        <div className="history-node-icon icon-gray">
          <Icon name="minus" size={14} />
        </div>
      );
    }

    return (
      <div className="history-node-icon icon-gray">
        <span className="dot" />
      </div>
    );
  };

  const propertyRent = property?.report_rent ?? property?.rent ?? 0;
  const propertyAddress = formatPropertyAddress(property, currentLang);
  const propertySubLocation = [
    property?.municipality || property?.city,
    property?.province,
    property?.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="property-history-container">
      {/* Property Summary Header Card */}
      <div className="rental-property-summary">
        <div className="rental-property-info">
          <div className="rental-property-icon">
            <Icon name="home" size={24} />
          </div>

          <div className="rental-property-text">
            <h3>{propertyAddress}</h3>
            <p>
              {[property?.province, property?.postcode]
                .filter(Boolean)
                .join(", ")}
            </p>
          </div>
        </div>

        <div className="rental-rent-card">
          <span className="rental-rent-label">{_t("propertyRent")}</span>

          <TextField
            id="history-property-rent"
            icon="€"
            trailing="/mo"
            placeholder="0"
            type="number"
            value={propertyRent}
            disabled={true}
          />
        </div>
      </div>

      {/* Activity Filter Header */}
      <div className="history-activity-section">
        <h6 className="activity-title">{_t("activity", "ACTIVITY")}</h6>
        <div className="activity-filter-pills">
          <Tabs
            size="sm"
            value={filter}
            onChange={handleFilterChange}
            items={[
              { value: "all", label: _t("all", "All") },
              { value: "tenants", label: _t("tenants", "Tenants") },
              { value: "awards", label: _t("awards", "Awards") },
            ]}
          />
        </div>
      </div>

      {/* Timeline Section */}
      {loading ? (
        <div className="py-5">
          <Loader />
        </div>
      ) : events.length === 0 ? (
        <div className="py-5 text-center text-muted">
          <p>{_t("noPropertyHistoryFound", "No property history found.")}</p>
        </div>
      ) : (
        <div className="history-timeline">
          <div className="timeline-line" />
          {events.map((event, index) => {
            const dateStr = moment(event.created_at || event.createdAt).format(
              "DD MMM YYYY · HH:mm"
            );
            const title = getEventTitle(event);
            const details = getEventDetails(event);
            const actorName = getActorName(event.actor);
            const workspaceName =
              event.actor_workspace?.name ||
              event.inherited_from_workspace?.name;
            const isAwardEvent =
              event.event_type === "property_awarded" &&
              Boolean(event.snapshot_url);

            return (
              <div key={event._id || index} className="timeline-item">
                {renderNodeIcon(event)}

                <div className="timeline-content">
                  <div className="timeline-date">{dateStr}</div>
                  <h5 className="timeline-event-title">{title}</h5>

                  {details && <p className="timeline-details">{details}</p>}

                  <div className="timeline-meta d-flex align-items-center gap-2">
                    {actorName && (
                      <span className="timeline-actor">
                        {_t("by", "by")} {actorName}
                      </span>
                    )}
                    {workspaceName && (
                      <Badge tone="neutral">{workspaceName}</Badge>
                    )}
                  </div>

                  {isAwardEvent && (
                    <div className="mt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleSnapshotClick(event)}
                        loading={snapshotLoadingId === event._id}
                        iconStart={<Icon name="fileText" size={16} />}
                      >
                        {snapshotLoadingId === event._id ? (
                          <>{_t("loading")}...</>
                        ) : (
                          _t("viewReportSnapshot", "View report snapshot")
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {pagination.page < pagination.totalPages && (
            <div className="text-center mt-3 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                loading={loadingMore}
              >
                {_t("loadMore", "Load more")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyHistoryTimeline;
